import { useEffect, useRef } from 'react';
import { loadModules } from 'esri-loader';
import { getSymbolForType } from './CostEstimationFunctions';
import {
  handleSketchCreate,
  handleSketchUpdate,
  handleSketchDelete,
  handleSketchUndo,
  handleSketchRedo
} from './CostEstimationSketchFunctions';

const CostEstimationWidget = ({ view, costEstimationEnabled, setCostEstimationEnabled }) => {
  const buttonRef = useRef(null);
  const sketchRef = useRef(null);
  const graphicsLayerRef = useRef(null);

  useEffect(() => {
    if (!view || !view.ui || buttonRef.current) return;

    const button = document.createElement('button');
    button.className = 'esri-widget';
    button.style.width = '32px';
    button.style.height = '32px';
    button.style.padding = '0';
    button.style.margin = '0';
    button.style.verticalAlign = 'middle';
    button.style.color = '#000';
    button.style.border = 'none';
    button.style.borderRadius = '4px';
    button.style.cursor = 'pointer';
    button.style.fontSize = '18px';
    button.style.lineHeight = '32px';
    button.style.textAlign = 'center';
    button.innerHTML = '€';
    button.title = 'Cost Estimation';

    button.onclick = () => {
      setCostEstimationEnabled(prev => !prev);
    };

    buttonRef.current = button;
    view.ui.add(button, { position: 'top-left', index: 8 });

    return () => {
      view.ui.remove(button);
      buttonRef.current = null;
    };
  }, [view, setCostEstimationEnabled]);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.style.backgroundColor = costEstimationEnabled ? '#ffffff92' : '#FFFFFF';
    }
  }, [costEstimationEnabled]);

  useEffect(() => {
    if (!view || !view.map) return;

    const cleanup = () => {
      if (sketchRef.current) {
        view.ui.remove(sketchRef.current);
        sketchRef.current.destroy();
        sketchRef.current = null;
      }
      if (graphicsLayerRef.current) {
        view.map.remove(graphicsLayerRef.current);
        graphicsLayerRef.current = null;
      }
      const summaryCard = document.getElementById("summary-card");
      if (summaryCard) {
        summaryCard.style.display = "none";
      }
      const pipelineFeatureTree = document.getElementById("pipeline-feature-tree");
      if (pipelineFeatureTree) {
        pipelineFeatureTree.style.display = "none";
      }      
    };

    if (!costEstimationEnabled) {
      cleanup();
      return;
    }

    const setupSketch = async () => {
      cleanup(); // Ensure previous instances are removed

      const [GraphicsLayer, Sketch, WFSLayer, Graphic] = await loadModules([
        'esri/layers/GraphicsLayer',
        'esri/widgets/Sketch',
        'esri/layers/WFSLayer',
        'esri/Graphic'
      ]);

      const graphicsLayer = new GraphicsLayer({ title: 'Cost Estimation Routes' });
      graphicsLayerRef.current = graphicsLayer;
      view.map.add(graphicsLayer);

      const wfsUrl = "https://portal.boom-h2-pipelines.com/geoserver/postgis/ows";
      const params = new URLSearchParams({
        service: "WFS",
        version: "2.0.0",
        request: "GetFeature",
        typeName: "postgis:redlines",
        outputFormat: "application/json"
      });

      const wfsResponse = await fetch(`${wfsUrl}?${params}`);
      const wfsGeoJson = await wfsResponse.json();

      wfsGeoJson.features.forEach(feature => {
        let geometry;
        const type = feature.geometry.type;
        const spatialRef = { wkid: 3857 };

        if (type === "LineString") {
          geometry = {
            type: "polyline",
            paths: [feature.geometry.coordinates],
            spatialReference: spatialRef
          };
        } else if (type === "MultiLineString") {
          geometry = {
            type: "polyline",
            paths: feature.geometry.coordinates,
            spatialReference: spatialRef
          };
        } else if (type === "Point") {
          geometry = {
            type: "point",
            x: feature.geometry.coordinates[0],
            y: feature.geometry.coordinates[1],
            spatialReference: spatialRef
          };
        } else if (type === "Polygon") {
          geometry = {
            type: "polygon",
            rings: feature.geometry.coordinates,
            spatialReference: spatialRef
          };
        }

        const graphic = new Graphic({
          geometry,
          attributes: {
            ...feature.properties,
            id: feature.id.replace('redlines.', '')
          },
          symbol: getSymbolForType(type)
        });

        graphicsLayer.add(graphic);
      });

      const sketch = new Sketch({
        layer: graphicsLayer,
        view,
        availableCreateTools: ['polyline'],
        creationMode: 'update'
      });

      sketch.viewModel.polylineSymbol = {
        type: 'simple-line',
        color: [255, 0, 0, 1],
        width: 2,
        style: 'dash'
      };

      sketchRef.current = sketch;
      view.ui.add(sketch, 'top-right');

      handleSketchCreate(sketch);
      handleSketchUpdate(sketch, graphicsLayer);
      handleSketchDelete(sketch);
      handleSketchRedo(sketch);
      handleSketchUndo(sketch);
    };

    setupSketch();

    return () => {
      cleanup();
    };
  }, [view, costEstimationEnabled]);

  return null;
};

export default CostEstimationWidget;
